<script setup lang="ts">
import { ref } from 'vue'

// An iOS-like search field: a glass inside on the left (placeholder-coloured until the field is focused or
// holds text) and a round (✕) on the right while there is text. Focus hides the placeholder.
defineProps<{
  placeholder: string
  clearLabel: string
}>()
const model = defineModel<string>({ required: true })
const input = ref<HTMLInputElement | null>(null)

function clear(): void {
  model.value = ''
  input.value?.focus()
}
</script>

<template>
  <div class="search-box">
    <svg class="glass" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="8.5" cy="8.5" r="5.5" fill="none" stroke="currentColor" stroke-width="2" />
      <path d="M13 13l4.5 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
    </svg>
    <input
      ref="input"
      v-model="model"
      type="search"
      class="ui-input field"
      :placeholder="placeholder"
      :aria-label="placeholder"
      @keydown.esc="model && (model = '')"
    />
    <button v-if="model" type="button" class="clear" :aria-label="clearLabel" @click="clear">
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="10" fill="currentColor" />
        <path
          d="M6.5 6.5l7 7M13.5 6.5l-7 7"
          stroke="var(--tg-bg-color)"
          stroke-width="2"
          stroke-linecap="round"
        />
      </svg>
    </button>
  </div>
</template>

<style scoped>
.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.field {
  padding-left: 2rem;
  padding-right: 2rem;
}

.field::placeholder {
  color: var(--tg-hint-color);
  opacity: 1;
}

.field:focus::placeholder {
  color: transparent;
}

.field::-webkit-search-cancel-button {
  display: none;
  appearance: none;
}

.glass {
  position: absolute;
  left: 0.6rem;
  width: 1rem;
  height: 1rem;
  color: var(--tg-hint-color);
  pointer-events: none;
}

.search-box:focus-within .glass,
.search-box:has(.clear) .glass {
  color: var(--tg-text-color);
}

.clear {
  position: absolute;
  right: 0.5rem;
  display: flex;
  padding: 0.2rem;
  border: 0;
  background: none;
  color: var(--tg-hint-color);
  cursor: pointer;
}

.clear svg {
  width: 1rem;
  height: 1rem;
}
</style>
